import {
  createEntityAdapter,
  createSelector,
  createSlice,
  EntityState,
  type PayloadAction,
} from "@reduxjs/toolkit";
import { RootState } from "../rootReducer";
import { Ingredient } from "../../models/ingredient";
import { deleteRecipeAction } from "../actions";
import { makeSyncUpsert, makeSyncRemove } from "../syncUpsert";
import { normalizeField } from "../../utils/textNormalize";

export const ingredientsAdapter = createEntityAdapter<Ingredient>();
type IngredientsState = EntityState<Ingredient, string>;
const initialState: IngredientsState = ingredientsAdapter.getInitialState();

type SaveIngredientsPayload = {
  recipeId: string;
  ingredients: Ingredient[];
};

function setIngredientsForRecipe(
  state: IngredientsState,
  recipeId: string,
  ingredients: Ingredient[],
): void {
  const now = new Date().toISOString();
  const desiredIds = new Set(ingredients.map((i) => i.id));

  // Soft-delete existing live rows for this recipe that are no longer present
  const updates = Object.values(state.entities)
    .filter((i) => i?.recipeId === recipeId && !desiredIds.has(i!.id) && !i!.deletedAt)
    .map((i) => ({ id: i!.id, changes: { deletedAt: now } }));
  ingredientsAdapter.updateMany(state, updates);

  // Upsert the desired set — clears any tombstone, stamps updatedAt, keeps createdAt.
  // sortOrder is stamped from array position (the list is already in display order),
  // so rows added via the form always have a non-null sort_order — not just the
  // import path that set it explicitly.
  const entries = ingredients.map((ingredient, index) => ({
    ...ingredient,
    // Clean paste artifacts from manually-entered/edited ingredient text on save.
    name: normalizeField(ingredient.name),
    // Normalise the substitute's name too; coerce undefined → null so removing
    // a substitute pushes an explicit null (clears the server value).
    substitute: ingredient.substitute
      ? { ...ingredient.substitute, name: normalizeField(ingredient.substitute.name) }
      : null,
    recipeId,
    sortOrder: index,
    deletedAt: undefined,
    createdAt: state.entities[ingredient.id]?.createdAt ?? ingredient.createdAt ?? now,
    updatedAt: now,
  }));
  ingredientsAdapter.upsertMany(state, entries);
}

export const ingredientsSlice = createSlice({
  name: "ingredients",
  initialState,
  reducers: {
    upsertManyFromSync: makeSyncUpsert(ingredientsAdapter),
    removeManyFromSync: makeSyncRemove(ingredientsAdapter),
    addIngredients(
      state: IngredientsState,
      action: PayloadAction<SaveIngredientsPayload>,
    ): void {
      const { recipeId, ingredients } = action.payload;
      setIngredientsForRecipe(state, recipeId, ingredients);
    },
    updateIngredients(
      state: IngredientsState,
      action: PayloadAction<SaveIngredientsPayload>,
    ): void {
      const { recipeId, ingredients } = action.payload;
      setIngredientsForRecipe(state, recipeId, ingredients);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(deleteRecipeAction, (state, action) => {
      const now = new Date().toISOString();
      const updates = Object.values(state.entities)
        .filter((i) => i?.recipeId === action.payload)
        .map((i) => ({
          id: i!.id,
          changes: { deletedAt: now },
        }));

      ingredientsAdapter.updateMany(state, updates);
    });
  },
});

const ingredientSelectors = ingredientsAdapter.getSelectors(
  (state: RootState) => state.ingredients,
);

export const selectIngredientById = ingredientSelectors.selectById;

// selectAll* hides soft-deleted rows; sync reads entities directly.
export const selectAllIngredients = createSelector(
  ingredientSelectors.selectAll,
  (ingredients) => ingredients.filter((i) => !i.deletedAt),
);

// TODO need a more efficient way to do this
export const selectIngredientsByRecipeId = createSelector(
  [selectAllIngredients, (_: RootState, recipeId: string) => recipeId],
  (ingredients, recipeId) =>
    // Sort by sortOrder so reorders take effect — the entity adapter keeps
    // upserted rows in their original id order regardless of the new sortOrder.
    ingredients
      .filter((rc) => rc.recipeId === recipeId)
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
);

export const {
  addIngredients,
  updateIngredients,
  upsertManyFromSync: upsertIngredientsFromSync,
  removeManyFromSync: removeIngredientsFromSync,
} = ingredientsSlice.actions;

export default ingredientsSlice.reducer;
